// Utility functions for managing menu cache

import { clearAllClientDataCache } from '../utils/clientProjectDataCache';

const MENU_CACHE_PREFIX = 'menu_';
const CHAT_CACHE_PREFIX = 'chat_';

const LEGACY_MENU_KEYS = ['menuCache', 'chatCountCache'] as const;

/** Remove every role/user menu and chat cache entry from localStorage. */
export const clearAllMenuCaches = () => {
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (
      key &&
      (key.startsWith(MENU_CACHE_PREFIX) || key.startsWith(CHAT_CACHE_PREFIX))
    ) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
  clearAllClientDataCache();

  LEGACY_MENU_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

/** Drop cached menu for one role (e.g. after Rights Management saves SME permissions). */
export const clearMenuCacheForRole = (roleId: number | string | null | undefined) => {
  if (roleId === null || roleId === undefined || roleId === '') {
    return;
  }
  localStorage.removeItem(`${MENU_CACHE_PREFIX}${roleId}`);
};

// Clear all menu-related caches and notify listeners to refetch
export const clearMenuCache = () => {
  clearAllMenuCaches();
  window.dispatchEvent(new CustomEvent('clearMenuCache'));
};

// Clear cache when user logs out
export const clearCacheOnLogout = () => {
  clearAllMenuCaches();
  clearAllClientDataCache();
  localStorage.removeItem('projectId');
  window.dispatchEvent(new CustomEvent('userLogout'));
};

// Clear cache when project changes
export const clearCacheOnProjectChange = () => {
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CHAT_CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
  localStorage.removeItem('chatCountCache');
  sessionStorage.removeItem('chatCountCache');
  clearAllClientDataCache();

  window.dispatchEvent(new CustomEvent('projectChanged'));
};

/**
 * Call after admin updates role menu permissions so open sessions refetch menus.
 * @param roleId - optional role whose cache should be cleared first
 */
export const clearCacheOnAdminChange = (roleId?: number | string | null) => {
  if (roleId !== null && roleId !== undefined && roleId !== '') {
    clearMenuCacheForRole(roleId);
  }
  clearAllMenuCaches();
  window.dispatchEvent(new CustomEvent('adminMenuChanged'));
};

// Check if cache is stale (older than 5 minutes)
export const isCacheStale = (cacheKey: string) => {
  const cacheData = localStorage.getItem(cacheKey);
  if (!cacheData) return true;

  try {
    const parsed = JSON.parse(cacheData);
    const now = Date.now();
    const cacheAge = now - (parsed.timestamp || 0);

    // Short-lived, but long enough to avoid refetch on every in-app navigation.
    return cacheAge > 15 * 60 * 1000;
  } catch {
    return true;
  }
};

// Get cache with timestamp
export const getCacheWithTimestamp = (key: string) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

// Set cache with timestamp
export const setCacheWithTimestamp = (key: string, data: unknown) => {
  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};
